package diana.soleil.design.iraniancanadienbazzar.core.common

/**
 * Pure, locale-agnostic input validators (no user-facing copy — error strings stay in string
 * resources). Deliberately lenient: they reject obviously-malformed input without imposing a strict
 * canonical format the user must guess. Shared so sign-up, claim, and listing forms agree.
 */
object Validators {

    /** A plausible email: `local@domain.tld` with a dot after the `@` and something after the dot. */
    fun isValidEmail(raw: String): Boolean {
        val value = raw.trim()
        val at = value.indexOf('@')
        val dot = value.lastIndexOf('.')
        return at > 0 && dot > at + 1 && dot < value.length - 1 && !value.contains(' ')
    }

    /**
     * A plausible phone number: 7–15 digits after stripping formatting (spaces, dashes, parens, a
     * leading `+`). Covers NANP (10) and international, rejects "abc" / too-short entries. Empty is
     * NOT valid here — callers treat an optional-and-blank field as "skip validation" themselves.
     */
    fun isValidPhone(raw: String): Boolean {
        val digits = raw.count { it.isDigit() }
        val allowed = raw.all { it.isDigit() || it in " -()+.".toList() }
        return allowed && digits in 7..15
    }
}

/** Coarse password strength buckets for the sign-up meter. `None` = empty field (render nothing). */
enum class PasswordStrength { None, Weak, Fair, Strong }

/**
 * Scores a password into a coarse strength bucket driven by length + character-class variety
 * (lowercase, uppercase, digit, symbol). Deliberately simple and offline — a UX nudge, not a security
 * gate (Firebase still enforces its own minimum). Empty → [PasswordStrength.None].
 */
fun passwordStrength(password: String): PasswordStrength {
    if (password.isEmpty()) return PasswordStrength.None
    val classes = listOf(
        password.any { it.isLowerCase() },
        password.any { it.isUpperCase() },
        password.any { it.isDigit() },
        password.any { !it.isLetterOrDigit() },
    ).count { it }
    return when {
        password.length >= 12 && classes >= 3 -> PasswordStrength.Strong
        password.length >= 8 && classes >= 2 -> PasswordStrength.Fair
        else -> PasswordStrength.Weak
    }
}
